import React, { RefObject, useCallback, useMemo } from 'react'
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import OrderIcon from '../../../assets/LearnScreen/order.svg'
import { DS } from '../../design-system'
import { translate } from '../../i18n'
import { ReduceMotion } from 'react-native-reanimated'
import { isWeb, useMaxWidth } from '../WebWrapper/WebWrapper'

type OutOfOrderSheetProps = {
  bottomSheetModalRef: RefObject<BottomSheetModal | null>
  okayPress: () => void
  ignorePress: () => void
}

export const OutOfOrderSheet = ({ bottomSheetModalRef, ignorePress, okayPress }: OutOfOrderSheetProps) => {
  const maxWidth = useMaxWidth()
  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />
    ),
    []
  )

  const percentage = Platform.OS === 'ios' ? '38%' : '45%'

  const snapPoints = useMemo(() => [percentage], [percentage])

  return (
    <View>
      <BottomSheetModal
        ref={bottomSheetModalRef}
        index={0}
        snapPoints={snapPoints}
        backdropComponent={renderBackdrop}
        detached={false}
        handleStyle={styles.bottomSheetHandleStyle}
        handleIndicatorStyle={styles.bottomSheetIndicatorStyle}
        keyboardBlurBehavior="restore"
        overrideReduceMotion={ReduceMotion.System}
        style={[
          {
            marginHorizontal: isWeb ? 'auto' : undefined,
            maxWidth: maxWidth,
            width: '100%'
          }
        ]}
      >
        <BottomSheetView style={{ flex: 1 }}>
          <DS.Frame style={styles.container}>
            <DS.Frame>
              <DS.Frame style={{ alignSelf: 'center' }}>
                <OrderIcon height={48} width={48} />
              </DS.Frame>
              <DS.Text variant="Heading 2/Medium" textAlign="center" style={{ marginTop: 8 }}>
                {translate('outOfOrderSheet.title')}
              </DS.Text>
              <DS.Text variant="Body Large/Regular" textAlign="center" style={{ marginTop: 8 }}>
                {translate('outOfOrderSheet.subtitle')}
              </DS.Text>
            </DS.Frame>
            <DS.Frame style={styles.actionsContainer}>
              <TouchableOpacity style={styles.okayButton} onPress={okayPress}>
                <DS.Text variant="Body Medium/Medium" color="Global/White" textAlign="center">
                  {translate('outOfOrderSheet.okayButton')}
                </DS.Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ignoreButton} onPress={ignorePress}>
                <DS.Text variant="Body Medium/Medium" color="Global/Black" textAlign="center">
                  {translate('outOfOrderSheet.ignoreButton')}
                </DS.Text>
              </TouchableOpacity>
            </DS.Frame>
          </DS.Frame>
        </BottomSheetView>
      </BottomSheetModal>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F6F2E9',
    paddingHorizontal: 32
  },
  bottomSheetHandleStyle: {
    backgroundColor: '#F6F2E9',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 0,
    height: 24
  },
  bottomSheetIndicatorStyle: {
    display: 'none'
  },
  okayButton: {
    paddingHorizontal: DS.spacing[24],
    paddingVertical: 12.5,
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: DS.spacing[24],
    width: '100%',
    height: 48
  },
  ignoreButton: {
    paddingHorizontal: DS.spacing[24],
    paddingVertical: 12.5,
    borderRadius: DS.spacing[24],
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    width: '100%',
    height: 48
  },
  actionsContainer: {
    flex: 1,
    paddingTop: 16,
    gap: 6
  }
})
